import type { DataOption } from '@/shared/components/HookForm/types'
import type {
	PricingCampaignModel,
	ProductAssignmentModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import { useMemo, useState } from 'react'
import {
	compact,
	filter,
	flatten,
	map,
	paths,
	pipe,
	prop,
	uniq,
} from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useDublicatePricingCampaignsMutation,
	useGlobalPcSetsQuery,
} from '@/entities/pricing-campaign'

import { client } from '@/shared/api/client'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { Spinner } from '@/shared/components/ui/spinner'
import { queryClient } from '@/shared/lib/query-client'
import { toOption } from '@/shared/tools/utils'

export const DuplicatePricingCampaignToGlobalPcSetsModal = ({
	isOpen,
	close,
	pricingCampaign,
}: {
	isOpen: boolean
	close: () => void
	pricingCampaign: PricingCampaignModel
}) => {
	const { t } = useTranslation()

	const [selectedGlobalPcSetId, setSelectedGlobalPcSetId] = useState<
		string | null
	>(null)
	const { data: globalPcSets, isLoading } = useGlobalPcSetsQuery({
		enabled: !!pricingCampaign,
	})
	const globalPcSetOptions = useMemo(
		() =>
			pipe(
				/**
				 * hide current global pc set and main global pc set (not allowed to duplicate to main global pc set)
				 */
				filter(
					({ id, is_main }) =>
						id !== pricingCampaign?.global_pc_set_id && !is_main
				),
				map(paths(['id', 'name'])),
				toOption
			)(globalPcSets),
		[globalPcSets, pricingCampaign]
	)
	const duplicatePricingCampaignToGlobalPcSetMutation =
		useDublicatePricingCampaignsMutation({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ['pricing-campaigns'],
				})
				queryClient.invalidateQueries({
					queryKey: ['pricing-campaigns-overview'],
				})
				close()
			},
		})
	const handleDuplicate = async () => {
		if (!selectedGlobalPcSetId) return

		toast.promise(
			duplicatePricingCampaignToGlobalPcSetMutation.mutateAsync({
				src_pricing_campaign_ids: [pricingCampaign.id],
				dst_entity_ids: [selectedGlobalPcSetId],
				dst_entity_type: 'global_pc_set',
			}),
			{
				loading: t('general_dublicating', { defaultValue: 'Duplicating...' }),
				success: t('general_duplicate', {
					defaultValue: 'Duplicated successfully',
				}),
				error: t('general_dublication_error', {
					defaultValue: 'Error while duplicating',
				}),
			}
		)
	}

	return (
		<ErrorBoundary>
			<Modal
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						close()
					}
				}}
			>
				<ModalContent className='max-w-xl'>
					<ModalHeader>
						<ModalTitle>
							{t('global_set.pc.duplicate_to.title', {
								defaultValue: 'Duplicate PC to Global Set',
							})}
						</ModalTitle>
						<ModalDescription>
							{t('global_set.pc.duplicate_to.description', {
								name: pricingCampaign?.name,
								defaultValue: `Select Global Set where you want to dublicate pricing campaign ${pricingCampaign?.name}	`,
							})}
						</ModalDescription>
					</ModalHeader>
					<ModalBody>
						<Select onValueChange={setSelectedGlobalPcSetId}>
							<SelectTrigger
								disabled={
									isLoading ||
									duplicatePricingCampaignToGlobalPcSetMutation.isPending ||
									!globalPcSetOptions?.length
								}
								className='w-full'
							>
								<SelectValue
									placeholder={t('global_set.select.placeholder', {
										defaultValue: 'Select global set',
									})}
								/>
							</SelectTrigger>
							<SelectContent className='max-w-(--radix-select-trigger-width)'>
								<SelectGroup>
									<SelectLabel>
										{t('global_set.select.label', {
											defaultValue: 'Global Sets',
										})}
									</SelectLabel>
									{globalPcSetOptions?.map((option: DataOption) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectGroup>
							</SelectContent>
						</Select>
					</ModalBody>
					<ModalFooter>
						<ModalClose asChild>
							<Button>{t('general_cancel')}</Button>
						</ModalClose>
						<Button
							variant='primary'
							disabled={
								isLoading ||
								!selectedGlobalPcSetId ||
								duplicatePricingCampaignToGlobalPcSetMutation.isPending
							}
							onClick={handleDuplicate}
						>
							{duplicatePricingCampaignToGlobalPcSetMutation.isPending && (
								<Spinner />
							)}
							{t('general_duplicate')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</ErrorBoundary>
	)
}
const getAffectedOptimizationGroups = async (
	pricingCampaign: PricingCampaignModel,
	t: TFunction
) => {
	const affectedOptimizationGroupsPromise = Promise.all([
		getMainGlobalSetAffectedOGs(),
		getAffectedOGsByProductAssignments(
			pricingCampaign.settings?.product_assignments || []
		),
	])
	toast.promise(affectedOptimizationGroupsPromise, {
		loading: t('checking.affected.optimization_groups', {
			defaultValue: 'Checking affected optimization groups...',
		}),
		error: t('error.checking.affected.optimization_groups', {
			defaultValue: 'Error while checking affected optimization groups',
		}),
	})
	const [mainGlobalSetAffectedOgs, affectedOGByProductAssignments] =
		await affectedOptimizationGroupsPromise

	return uniq([
		...(mainGlobalSetAffectedOgs || []),
		...(affectedOGByProductAssignments || []),
	])
}
const getAffectedOGsByProductAssignments = async (
	productAssignments: ProductAssignmentModel
) =>
	await client.products
		.getProductsCountByPc({
			filters: pipe(map(prop('filters')), flatten)(productAssignments) || [],
		})
		.then(pipe(map(prop('optimization_group_id')), compact, uniq))

const getMainGlobalSetAffectedOGs = async () => {
	const mainGlobalSetPcIds = await client.pricingCampaign
		.pricingCampaignList({
			only_global: true,
		})
		.then(pipe(map(prop('id')), compact))

	return await client.products
		.getProductsCountByPc({
			filters: [
				{
					name: 'pricing_campaign_id',
					operation: Operators.IN as const,
					value: mainGlobalSetPcIds,
				},
			],
		})
		.then(pipe(map(prop('optimization_group_id')), compact, uniq))
}
